import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, ChevronDown, X, MapPin } from "lucide-react";
import { Input } from "./input";
import { RootState } from "../../redux/store";
import { fetchRegions } from "../../redux/Slices/LocationSlice";

interface Region {
  id: string;
  name: string;
  country?: string;
  state?: string;
  city?: string;
  initial?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AdminRegionDropdownProps {
  selectedRegion: Region | null;
  onRegionSelect: (region: Region | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const AdminRegionDropdown: React.FC<AdminRegionDropdownProps> = ({
  selectedRegion,
  onRegionSelect,
  placeholder = "Select region...",
  className = "",
  disabled = false
}) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { regions, regionsLoading } = useSelector((state: RootState) => (state as any).locations || {
    regions: [],
    regionsLoading: false
  });

  const filteredRegions = regions.filter((region: Region) =>
    region.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (region.country && typeof region.country === "string" && region.country.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (region.state && typeof region.state === "string" && region.state.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (region.city && typeof region.city === "string" && region.city.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Load regions on component mount
  useEffect(() => {
    if (regions.length === 0 && !regionsLoading) {
      dispatch(fetchRegions({ page: 1, limit: 100 }) as any);
    }
  }, [dispatch, regions.length, regionsLoading]);

  // Handle search with debounce
  const handleSearch = useCallback((query: string) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (query.trim()) {
        dispatch(fetchRegions({ 
          page: 1, 
          limit: 100,
          search: query
        }) as any);
      }
    }, 300);

    setSearchTimeout(timeout);
  }, [dispatch, searchTimeout]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleRegionSelect = (region: Region) => {
    console.log("Selected region:", region);
    onRegionSelect(region);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between px-3 py-2 border rounded-lg min-h-[42px]
          ${disabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          }
          transition-colors
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedRegion ? (
            <>
              <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="truncate">{selectedRegion.name || "Unknown Region"}</span>
              {selectedRegion.country && typeof selectedRegion.country === "string" && (
                <span className="text-gray-400 text-sm">({selectedRegion.country})</span>
              )}
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedRegion && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRegionSelect(null);
              }}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="h-3 w-3 text-gray-400" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search regions..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                className="pl-10"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {regionsLoading && regions.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-500">Loading regions...</span>
              </div>
            ) : filteredRegions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <MapPin className="h-8 w-8 text-gray-300 mb-2" />
                <p>No regions found</p>
                {searchQuery && (
                  <p className="text-sm text-gray-400">Try a different search term</p>
                )}
              </div>
            ) : (
              filteredRegions.map((region: Region) => (
                <button
                  key={region.id}
                  type="button"
                  onClick={() => handleRegionSelect(region)}
                  className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 transition-colors ${
                    selectedRegion?.id === region.id ? "bg-blue-50 border-l-4 border-blue-500" : ""
                  }`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-gray-500" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{region.name || "Unknown Region"}</p>
                    <p className="text-sm text-gray-500 truncate">
                      {[
                        typeof region.city === "string" ? region.city : "", 
                        typeof region.state === "string" ? region.state : "", 
                        typeof region.country === "string" ? region.country : ""
                      ].filter(Boolean).join(", ") || "Region"}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRegionDropdown;
